import React, { useState, useEffect, useCallback } from 'react';
import { customFieldsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AppDrawer } from '../components/ui/AppDrawer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Loader2, Trash2, Edit2, ArrowLeft, Layers, TextCursorInput, Hash, Calendar, ChevronDown, CheckSquare, Type, X } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const FIELD_TYPES = [
  { value: 'text',     label: 'Short Text',  icon: Type },
  { value: 'textarea', label: 'Long Text',   icon: TextCursorInput },
  { value: 'number',   label: 'Number',      icon: Hash },
  { value: 'currency', label: 'Currency',     icon: Hash },
  { value: 'date',     label: 'Date',        icon: Calendar },
  { value: 'dropdown', label: 'Dropdown',    icon: ChevronDown },
  { value: 'checkbox', label: 'Checkbox',    icon: CheckSquare },
];

function getTypeIcon(type) {
  const ft = FIELD_TYPES.find(f => f.value === type);
  return ft ? ft.icon : Type;
}

function getTypeLabel(type) {
  const ft = FIELD_TYPES.find(f => f.value === type);
  return ft ? ft.label : type;
}

export default function CustomFieldSettings() {
  const navigate = useNavigate();
  const { isOwner, isOffice } = useAuth();
  const canEdit = isOwner || isOffice;

  const [tab, setTab] = useState('fields');
  const [fields, setFields] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Field drawer
  const [fieldDrawer, setFieldDrawer] = useState(false);
  const [editField, setEditField] = useState(null);
  const [fieldForm, setFieldForm] = useState({ label: '', field_type: 'text', options: '', default_value: '', is_required: false });
  const [saving, setSaving] = useState(false);

  // Template drawer
  const [templateDrawer, setTemplateDrawer] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', field_ids: [] });

  const fetchAll = useCallback(async () => {
    try {
      const [fRes, tRes] = await Promise.all([
        customFieldsAPI.listDefinitions(),
        customFieldsAPI.listTemplates(),
      ]);
      setFields(fRes.data.fields || []);
      setTemplates(tRes.data.templates || []);
    } catch { toast.error('Failed to load custom fields'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ---- Field CRUD ----
  const openFieldCreate = () => {
    setEditField(null);
    setFieldForm({ label: '', field_type: 'text', options: '', default_value: '', is_required: false });
    setFieldDrawer(true);
  };

  const openFieldEdit = (f) => {
    setEditField(f);
    setFieldForm({
      label: f.label, field_type: f.field_type,
      options: (f.options || []).join(', '),
      default_value: f.default_value || '', is_required: f.is_required,
    });
    setFieldDrawer(true);
  };

  const handleFieldSave = async () => {
    if (!fieldForm.label.trim()) { toast.error('Label is required'); return; }
    setSaving(true);
    try {
      const payload = {
        label: fieldForm.label.trim(),
        field_type: fieldForm.field_type,
        options: fieldForm.field_type === 'dropdown' ? fieldForm.options.split(',').map(s => s.trim()).filter(Boolean) : undefined,
        default_value: fieldForm.default_value || undefined,
        is_required: fieldForm.is_required,
      };
      if (editField) {
        await customFieldsAPI.updateDefinition(editField.id, payload);
        toast.success('Field updated');
      } else {
        await customFieldsAPI.createDefinition(payload);
        toast.success('Field created');
      }
      setFieldDrawer(false);
      fetchAll();
    } catch { toast.error('Failed to save field'); }
    finally { setSaving(false); }
  };

  const handleFieldDelete = async (id) => {
    if (!window.confirm('Delete this field? Values will be lost for all jobs using it.')) return;
    try {
      await customFieldsAPI.deleteDefinition(id);
      toast.success('Field deleted');
      fetchAll();
    } catch { toast.error('Failed to delete field'); }
  };

  // ---- Template CRUD ----
  const openTemplateCreate = () => {
    setEditTemplate(null);
    setTemplateForm({ name: '', description: '', field_ids: [] });
    setTemplateDrawer(true);
  };

  const openTemplateEdit = (t) => {
    setEditTemplate(t);
    setTemplateForm({ name: t.name, description: t.description || '', field_ids: t.field_ids || [] });
    setTemplateDrawer(true);
  };

  const handleTemplateSave = async () => {
    if (!templateForm.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: templateForm.name.trim(),
        description: templateForm.description || undefined,
        field_ids: templateForm.field_ids,
      };
      if (editTemplate) {
        await customFieldsAPI.updateTemplate(editTemplate.id, payload);
        toast.success('Field set updated');
      } else {
        await customFieldsAPI.createTemplate(payload);
        toast.success('Field set created');
      }
      setTemplateDrawer(false);
      fetchAll();
    } catch { toast.error('Failed to save field set'); }
    finally { setSaving(false); }
  };

  const handleTemplateDelete = async (id) => {
    if (!window.confirm('Delete this field set?')) return;
    try {
      await customFieldsAPI.deleteTemplate(id);
      toast.success('Field set deleted');
      fetchAll();
    } catch { toast.error('Failed to delete field set'); }
  };

  const toggleFieldInTemplate = (fieldId) => {
    setTemplateForm(prev => {
      const ids = prev.field_ids.includes(fieldId)
        ? prev.field_ids.filter(id => id !== fieldId)
        : [...prev.field_ids, fieldId];
      return { ...prev, field_ids: ids };
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="custom-field-settings">
        {/* Header */}
        <div>
          <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2 transition-colors" data-testid="cf-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="text-2xl font-bold">Custom Fields</h1>
          <p className="text-sm text-muted-foreground mt-1">Define custom fields and group them into field sets. These can be attached to any service or project job.</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="fields" data-testid="cf-tab-fields">Fields ({fields.length})</TabsTrigger>
            <TabsTrigger value="sets" data-testid="cf-tab-sets">Field Sets ({templates.length})</TabsTrigger>
          </TabsList>

          {/* ===== Fields Tab ===== */}
          <TabsContent value="fields" className="mt-4">
            <div className="flex justify-end mb-4">
              {canEdit && (
                <Button size="sm" onClick={openFieldCreate} data-testid="new-field-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Field
                </Button>
              )}
            </div>
            {fields.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <TextCursorInput className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No custom fields yet</p>
                <p className="text-sm mt-1">Create fields like "Permit Number", "Utility Company", etc.</p>
              </div>
            ) : (
              <div className="border rounded-lg divide-y" data-testid="field-list">
                {fields.map(f => {
                  const Icon = getTypeIcon(f.field_type);
                  return (
                    <div key={f.id} className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer group" onClick={() => openFieldEdit(f)} data-testid={`field-row-${f.id}`}>
                      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium">{f.label}</p>
                          {f.is_required && <Badge variant="secondary" className="text-[10px] h-4">Required</Badge>}
                          {!f.is_active && <Badge variant="outline" className="text-[10px] h-4 text-muted-foreground">Inactive</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{getTypeLabel(f.field_type)}{f.field_type === 'dropdown' && f.options?.length ? ` (${f.options.length} options)` : ''}</p>
                      </div>
                      {canEdit && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100" onClick={e => { e.stopPropagation(); handleFieldDelete(f.id); }} data-testid={`delete-field-${f.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ===== Field Sets Tab ===== */}
          <TabsContent value="sets" className="mt-4">
            <div className="flex justify-end mb-4">
              {canEdit && (
                <Button size="sm" onClick={openTemplateCreate} data-testid="new-set-btn">
                  <Plus className="w-4 h-4 mr-1" /> New Field Set
                </Button>
              )}
            </div>
            {templates.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Layers className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No field sets yet</p>
                <p className="text-sm mt-1">Group fields into sets like "HVAC Job Fields" or "Roofing Project Fields".</p>
              </div>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2" data-testid="template-list">
                {templates.map(t => (
                  <Card key={t.id} className="cursor-pointer hover:border-primary/30 transition-colors" onClick={() => openTemplateEdit(t)} data-testid={`template-card-${t.id}`}>
                    <CardHeader className="pb-2 pt-4 px-4">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">{t.name}</CardTitle>
                        {canEdit && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={e => { e.stopPropagation(); handleTemplateDelete(t.id); }} data-testid={`delete-template-${t.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                    </CardHeader>
                    <CardContent className="px-4 pb-4">
                      <div className="flex flex-wrap gap-1">
                        {t.fields?.map(f => (
                          <Badge key={f.id} variant="secondary" className="text-[10px]">{f.label}</Badge>
                        ))}
                        {(!t.fields || t.fields.length === 0) && <span className="text-xs text-muted-foreground">No fields</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Field Create/Edit Drawer */}
        <AppDrawer open={fieldDrawer} onOpenChange={setFieldDrawer} title={editField ? 'Edit Field' : 'New Field'} description={editField ? `Editing "${editField.label}"` : 'Create a reusable custom field'}>
          <div className="space-y-4 p-1">
            <div>
              <Label>Label *</Label>
              <Input autoFocus value={fieldForm.label} onChange={e => setFieldForm(f => ({...f, label: e.target.value}))} placeholder="e.g. Permit Number, Utility Company..." data-testid="cf-label-input" />
            </div>
            <div>
              <Label>Field Type</Label>
              <Select value={fieldForm.field_type} onValueChange={v => setFieldForm(f => ({...f, field_type: v}))}>
                <SelectTrigger data-testid="cf-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map(ft => <SelectItem key={ft.value} value={ft.value}>{ft.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {fieldForm.field_type === 'dropdown' && (
              <div>
                <Label>Options (comma-separated)</Label>
                <Input value={fieldForm.options} onChange={e => setFieldForm(f => ({...f, options: e.target.value}))} placeholder="Option 1, Option 2, Option 3" data-testid="cf-options-input" />
              </div>
            )}
            <div>
              <Label>Default Value</Label>
              <Input value={fieldForm.default_value} onChange={e => setFieldForm(f => ({...f, default_value: e.target.value}))} placeholder="Optional" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={fieldForm.is_required} onCheckedChange={v => setFieldForm(f => ({...f, is_required: v}))} id="cf-required" data-testid="cf-required-switch" />
              <Label htmlFor="cf-required" className="cursor-pointer">Required field</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleFieldSave} disabled={saving} className="flex-1" data-testid="save-field-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editField ? 'Update Field' : 'Create Field'}
              </Button>
              {editField && canEdit && (
                <Button variant="destructive" size="icon" onClick={() => { handleFieldDelete(editField.id); setFieldDrawer(false); }}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </AppDrawer>

        {/* Template Create/Edit Drawer */}
        <AppDrawer open={templateDrawer} onOpenChange={setTemplateDrawer} title={editTemplate ? 'Edit Field Set' : 'New Field Set'} description="Group fields together for easy assignment to jobs">
          <div className="space-y-4 p-1">
            <div>
              <Label>Name *</Label>
              <Input autoFocus value={templateForm.name} onChange={e => setTemplateForm(f => ({...f, name: e.target.value}))} placeholder="e.g. HVAC Job Fields, Roofing Fields..." data-testid="template-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={templateForm.description} onChange={e => setTemplateForm(f => ({...f, description: e.target.value}))} rows={2} placeholder="Optional description..." />
            </div>
            <div>
              <Label>Fields in this Set</Label>
              <p className="text-xs text-muted-foreground mb-2">Select which custom fields belong to this set.</p>
              {fields.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No fields created yet. Create fields first.</p>
              ) : (
                <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                  {fields.filter(f => f.is_active).map(f => {
                    const Icon = getTypeIcon(f.field_type);
                    const selected = templateForm.field_ids.includes(f.id);
                    return (
                      <label key={f.id} className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-muted/30 ${selected ? 'bg-primary/5' : ''}`}>
                        <input type="checkbox" checked={selected} onChange={() => toggleFieldInTemplate(f.id)} className="rounded" />
                        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm">{f.label}</span>
                        <span className="text-[10px] text-muted-foreground ml-auto">{getTypeLabel(f.field_type)}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleTemplateSave} disabled={saving} className="flex-1" data-testid="save-template-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {editTemplate ? 'Update Field Set' : 'Create Field Set'}
              </Button>
              {editTemplate && canEdit && (
                <Button variant="destructive" size="icon" onClick={() => { handleTemplateDelete(editTemplate.id); setTemplateDrawer(false); }}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </AppDrawer>
      </div>
    </Layout>
  );
}
